import { useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

/**
 * Хендлер нативного события типа `MouseEvent` (i.e. `mousedown`, 'mouseup', etc.)
 */
type NativeMouseEventHandler = (event: MouseEvent) => void;

/**
 * Хук предоставляет методы для подписки и отписки на событие `mousedown` в элементе `document`.
 * Подписка позволяет отслеживать, произошел ли клик по ноде, не являющейся дочерней для заданного элемента
 */
export const useOutsideClickSubscriber = () => {
    /**
     * Реф на слушатель события, создаваемый в функции подписки
     */
    const handlerRef = useRef<NativeMouseEventHandler | null>(null);

    /**
     * Создать функцию слушателя события `mousedown`.
     * Принимает реф на DOM-ноду и коллбек.
     * Слушатель вызовет переданный коллбек только если клик произойдет по ноде, являющейся
     * дочерней для `nodeRef.current`
     * @param nodeRef реф на ноду, для которой производится проверка вложенности
     * @param callOnOutsideClick коллбек, который будет вызван, если клик произошел
     * *не внутри* `nodeRef.current`
     */
    const subscribeToOutsideClick = (
        nodeRef: MutableRefObject<HTMLElement | null>,
        callOnOutsideClick: () => void,
    ) => {
        const handler: NativeMouseEventHandler = event => {
            const isOutsideClick = nodeRef.current && event.target && !nodeRef.current.contains(event.target as Node);
            if (isOutsideClick) callOnOutsideClick();
        };

        handlerRef.current = handler;
        document.addEventListener('mousedown', handler);
    };

    /** Перестать слушать событие `mousedown` */
    const unsubscribeFromOutsideClick = () => {
        if (handlerRef.current) {
            document.removeEventListener('mousedown', handlerRef.current);
            handlerRef.current = null;
        }
    };

    useEffect(() => unsubscribeFromOutsideClick, []);

    return useMemo(() => ({
        subscribeToOutsideClick,
        unsubscribeFromOutsideClick,
    }), []);
};
